import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Le mot de passe doit contenir au moins 8 caractères."); return; }
    if (password !== confirm) { setError("Les deux mots de passe ne correspondent pas."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Réinitialisation impossible.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-ink text-[#F3F4F6] font-body flex items-center justify-center p-6">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="w-full max-w-md">
        <Link to="/connexion" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-[#A0B2CE] hover:text-gold transition-colors mb-10" data-testid="reset-back-login">
          <ArrowLeft className="w-4 h-4" /> Retour à la connexion
        </Link>

        {!token ? (
          <div data-testid="reset-invalid">
            <h1 className="font-serif text-4xl mb-3">Lien incomplet</h1>
            <p className="text-[#A0B2CE]">Ce lien de réinitialisation est incomplet. Utilisez le lien complet reçu par email, ou redemandez-en un depuis la page de connexion (« Mot de passe oublié ? »).</p>
          </div>
        ) : done ? (
          <div data-testid="reset-success">
            <CheckCircle2 className="w-10 h-10 text-gold mb-6" />
            <h1 className="font-serif text-4xl mb-3">Mot de passe modifié</h1>
            <p className="text-[#A0B2CE] mb-8">Votre nouveau mot de passe est enregistré. Vous pouvez vous connecter.</p>
            <Link to="/connexion" data-testid="reset-goto-login"
              className="inline-flex items-center justify-center gap-3 bg-gold text-ink px-8 py-4 rounded-full font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#B5952F] transition-colors duration-300">
              Se connecter
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-serif text-4xl mb-3">Nouveau mot de passe</h1>
            <p className="text-[#A0B2CE] mb-8">Choisissez votre nouveau mot de passe (8 caractères minimum).</p>
            <form onSubmit={submit} className="space-y-6" data-testid="reset-form">
              <div>
                <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">Nouveau mot de passe</label>
                <div className="relative">
                  <input type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password"
                    className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 pr-10 text-lg transition-colors duration-300" />
                  <button type="button" onClick={() => setShow((v) => !v)} data-testid="toggle-reset-password"
                    title={show ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-[#A0B2CE] hover:text-gold transition-colors">
                    {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">Confirmer le mot de passe</label>
                <input type={show ? "text" : "password"} required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-password-confirm"
                  className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-lg transition-colors duration-300" />
              </div>
              {error && <p className="text-red-400 text-sm font-mono" data-testid="reset-error">{error}</p>}
              <button type="submit" disabled={busy} data-testid="reset-submit"
                className="w-full inline-flex items-center justify-center gap-3 bg-gold text-ink py-4 rounded-full font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#B5952F] transition-colors duration-300 disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer le mot de passe"}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
